import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import { api, ApiError } from '../lib/api-client.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Select } from '../components/ui/input.js';
import { FormBanner } from '../components/ui/form.js';

/**
 * CSV import for contact profiles.
 *
 * Two-step flow: (1) parse the file in the browser with PapaParse and
 * show a column-mapping table so the operator can match CSV headers to
 * ContactProfile fields, (2) POST the mapped rows to /v1/contact-profiles/batch
 * in chunks of 500. The server returns a per-row result; we render the
 * failures inline so the operator can fix and re-upload only the bad rows.
 *
 * No server-side file storage — the CSV never leaves the browser as a
 * file; we send already-validated JSON objects.
 */

interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

interface BatchRowResult {
  index: number;
  ok: boolean;
  id?: string;
  error?: string;
}

interface BatchResponse {
  created: number;
  failed: number;
  results: BatchRowResult[];
}

/** Field name → label for the mapping UI. */
const TARGET_FIELDS: { value: string; label: string; required?: boolean }[] = [
  { value: 'name', label: 'Name (internal label)', required: true },
  { value: 'contact_name', label: 'Contact name' },
  { value: 'organization_name', label: 'Organization name' },
  { value: 'direct_number', label: 'Caller-ID number (E.164)' },
  { value: 'reply_to_email', label: 'Reply-to email' },
  { value: 'timezone', label: 'Timezone (IANA)' },
];

const CHUNK_SIZE = 500;

export function ContactProfilesImportPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [allResults, setAllResults] = useState<BatchRowResult[]>([]);
  const [summary, setSummary] = useState<{ created: number; failed: number } | null>(null);

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    setSummary(null);
    setAllResults([]);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        if (res.errors.length) {
          setParseError(`CSV parse error: ${res.errors[0]?.message ?? 'unknown'}`);
          return;
        }
        const headers = res.meta.fields ?? [];
        if (headers.length === 0) {
          setParseError('No columns detected. Does the file have a header row?');
          return;
        }
        setParsed({ headers, rows: res.data });
        // Auto-map columns whose header matches a target field (case-insensitive).
        const auto: Record<string, string> = {};
        for (const t of TARGET_FIELDS) {
          const found = headers.find(
            (h) => h.toLowerCase().replace(/[\s_-]/g, '') === t.value.replace(/_/g, ''),
          );
          if (found) auto[t.value] = found;
        }
        setMapping(auto);
      },
      error: (err) => setParseError(err.message),
    });
  };

  const ready = useMemo(() => {
    if (!parsed) return false;
    // 'name' is required by ContactProfileCreateSchema.
    return !!mapping['name'];
  }, [parsed, mapping]);

  const importMut = useMutation({
    mutationFn: async () => {
      if (!parsed) throw new Error('no_csv');
      const objs = parsed.rows.map((row) => {
        const obj: Record<string, string> = {};
        for (const [target, source] of Object.entries(mapping)) {
          if (!source) continue;
          const v = (row[source] ?? '').trim();
          if (v) obj[target] = v;
        }
        return obj;
      });

      let createdTotal = 0;
      let failedTotal = 0;
      const all: BatchRowResult[] = [];
      const chunks = Math.ceil(objs.length / CHUNK_SIZE);
      const chunkErrors: string[] = [];
      setProgress({ done: 0, total: chunks });

      // Per-chunk try/catch: a network blip or server error on chunk N must
      // NOT discard the partial-success summary for chunks 0..N-1, because
      // those rows are already persisted server-side. Operators must see
      // exactly which chunk failed so they only re-upload the missing
      // slice — re-uploading the whole file would create duplicates.
      for (let i = 0; i < chunks; i++) {
        const slice = objs.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        try {
          const res = await api.post<BatchResponse>('/v1/contact-profiles/batch', { rows: slice });
          createdTotal += res.created;
          failedTotal += res.failed;
          for (const r of res.results) {
            all.push({ ...r, index: i * CHUNK_SIZE + r.index });
          }
        } catch (err) {
          const startRow = i * CHUNK_SIZE + 2; // +1 for header, +1 for 1-indexed
          const endRow = Math.min(startRow + slice.length - 1, startRow + CHUNK_SIZE - 1);
          const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
          chunkErrors.push(`Rows ${startRow}–${endRow} (chunk ${i + 1}/${chunks}): ${msg}`);
          failedTotal += slice.length;
          for (let k = 0; k < slice.length; k++) {
            all.push({
              index: i * CHUNK_SIZE + k,
              ok: false,
              error: `chunk upload failed: ${msg}`,
            });
          }
        }
        setProgress({ done: i + 1, total: chunks });
        // Stream partial results so the operator sees progress even on big files.
        setAllResults([...all]);
        setSummary({ created: createdTotal, failed: failedTotal });
      }
      if (chunkErrors.length) {
        setParseError(
          `Some chunks failed — re-upload ONLY the listed row ranges to avoid duplicates:\n` +
            chunkErrors.join('\n'),
        );
      }
      qc.invalidateQueries({ queryKey: ['contact-profiles'] });
    },
    onError: (err) => setParseError(extractDetail(err)),
  });

  const failureRows = allResults.filter((r) => !r.ok);

  return (
    <div className="max-w-4xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Import contact profiles from CSV</h1>
          <p className="text-sm text-ink-muted">
            Upload a CSV with a header row, map columns to fields, then run the import. Rows
            with validation errors are skipped — successful rows are saved.
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/contact-profiles')}>
          ← Back to list
        </Button>
      </header>

      {parseError && <FormBanner message={parseError} />}

      <Card>
        <CardHeader>
          <CardTitle>1. Choose CSV file</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            className="text-sm file:mr-3 file:rounded file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-sm file:text-ink-on-accent"
          />
          {parsed && (
            <p className="text-xs text-ink-muted">
              Detected {parsed.rows.length} row(s) and {parsed.headers.length} column(s).
            </p>
          )}
        </CardBody>
      </Card>

      {parsed && (
        <Card>
          <CardHeader>
            <CardTitle>2. Map columns</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <p className="text-xs text-ink-muted">
              Pick the CSV column that supplies each field. Unmapped fields stay empty.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-4 font-medium">Field</th>
                  <th className="py-2 pr-4 font-medium">CSV column</th>
                  <th className="py-2 font-medium">Sample</th>
                </tr>
              </thead>
              <tbody>
                {TARGET_FIELDS.map((t) => {
                  const source = mapping[t.value] ?? '';
                  const sample = source ? parsed.rows[0]?.[source] ?? '' : '';
                  return (
                    <tr key={t.value} className="border-b border-border">
                      <td className="py-2 pr-4">
                        {t.label}{' '}
                        {t.required && <span className="text-danger" title="Required">*</span>}
                      </td>
                      <td className="py-2 pr-4">
                        <Select
                          value={source}
                          onChange={(e) =>
                            setMapping({ ...mapping, [t.value]: e.target.value })
                          }
                        >
                          <option value="">— Not mapped —</option>
                          {parsed.headers.map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </Select>
                      </td>
                      <td className="py-2 truncate max-w-[16rem] text-xs text-ink-muted" title={sample}>
                        {sample || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {parsed && (
        <Card>
          <CardHeader>
            <CardTitle>3. Run import</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <Button
              onClick={() => importMut.mutate()}
              disabled={!ready || importMut.isPending}
            >
              {importMut.isPending
                ? `Importing… (chunk ${progress?.done ?? 0}/${progress?.total ?? '?'})`
                : `Import ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}`}
            </Button>
            {!ready && (
              <p className="text-xs text-warning">
                Map the required <strong>Name</strong> field to enable Import.
              </p>
            )}

            {summary && (
              <div className="rounded border border-border bg-surface-alt p-3 text-sm space-y-1">
                <div>
                  <span className="font-medium text-success">Created:</span> {summary.created}
                </div>
                <div>
                  <span className="font-medium text-danger">Failed:</span> {summary.failed}
                </div>
              </div>
            )}

            {failureRows.length > 0 && (
              <div className="space-y-1">
                <h3 className="text-sm font-medium">Failed rows ({failureRows.length})</h3>
                <ul className="max-h-72 overflow-auto rounded border border-border text-xs">
                  {failureRows.slice(0, 200).map((r) => (
                    <li
                      key={r.index}
                      className="border-b border-border px-2 py-1 last:border-b-0"
                    >
                      <span className="text-ink-muted">Row {r.index + 2}:</span>{' '}
                      <span className="text-danger">{r.error}</span>
                    </li>
                  ))}
                  {failureRows.length > 200 && (
                    <li className="px-2 py-1 text-ink-muted">
                      …and {failureRows.length - 200} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
